// ─────────────────────────────────────────────────────────────────────────────
// CtaBanner.jsx — shared component (src/components/common/CtaBanner.jsx)
//
// MIAVIT layout:
//   Image RIGHT side (absolute, full height, z-index:1)
//   Green box BOTTOM-LEFT (absolute, 55% wide, z-index:2 — sits ON TOP of image)
//
// Usage — drop on any page with zero config:
//   import CtaBanner from '@components/common/CtaBanner'
//   <CtaBanner />
//
// Or customise per page:
//   <CtaBanner
//     tag="Get Started"
//     title="Ready to improve your herd's performance?"
//     body="Contact our expert team to discuss the right solution for your operation."
//     buttonText="Contact Us"
//     buttonTo="/contact"
//   />
//
// Pages that use this:
//   HomePage, AboutPage, ProductsPage, ProductDetailPage, ContactPage
// ─────────────────────────────────────────────────────────────────────────────

import Button from '@components/common/Button'

// Uncomment when image is ready:
import ctaImg from '@assets/images/cta/customer-care.jpg'
// const ctaImg = null

export default function CtaBanner({
  tag        = 'Contact',
  title      = "Let's talk about nutrition!",
  body       = "At Salient we source and distribute premium nutritional solutions for the animal nutrition industry. We also offer technical guidance and expert support. Interested?",
  buttonText = 'Contact us',
  buttonTo   = '/contact',
  buttonHref,   // use this instead of buttonTo for external links
}) {
  return (
    <section
      className="cta-section"
      style={{
        position:   'relative',
        width:      '100%',
        minHeight:  'clamp(280px, 36vw, 440px)',
        background: '#ffffff',
        overflow:   'hidden',
      }}
    >

      {/* ── IMAGE — right side, full height, BEHIND green box ── */}
      <div
        className="cta-photo"
        style={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0,
          width:    '55%',
          zIndex:   1,
          overflow: 'hidden',
        }}
      >
        {ctaImg ? (
          <img
            src={ctaImg}
            alt="Contact Salient Agrinutrition"
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center',
              display: 'block',
            }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: 'linear-gradient(135deg, #d0ddd8 0%, #b8ccc4 100%)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '10px',
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
              stroke="#8aaa9e" strokeWidth="1" strokeLinecap="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'11px',
              color:'#8aaa9e', margin:0, textAlign:'center', lineHeight:1.6 }}>
              src/assets/images/cta/cta-bg.jpg
            </p>
          </div>
        )}
      </div>

      {/* ── GREEN BOX — bottom-left, ON TOP of image ── */}
      <div
        className="cta-green-box"
        style={{
          position:       'absolute',
          bottom:         0, left: 0,
          width:          '55%',
          zIndex:         2,
          background:     '#007935',
          padding:        'clamp(28px, 4vw, 52px) clamp(20px, 4vw, 56px)',
        }}
      >
        {/* Tag label */}
        <p style={{
          fontFamily:    "'DM Sans', sans-serif",
          fontSize:      '13px', fontWeight: 400,
          letterSpacing: '0.13em', textTransform: 'uppercase',
          color:         'rgba(255,255,255,0.60)',
          margin:        '0 0 12px',
        }}>
          {tag}
        </p>

        {/* Heading */}
        <h2 style={{
          fontFamily:    "'DM Sans', sans-serif",
          fontSize:      'clamp(1.5rem, 2.8vw, 2.1rem)',
          fontWeight:    400,
          letterSpacing: '0.04em', textTransform: 'uppercase',
          color:         '#ffffff',
          margin:        '0 0 14px',
        }}>
          {title}
        </h2>

        {/* Body */}
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize:   'clamp(13px, 1.1vw, 14px)',
          color:      'rgba(255,255,255,0.82)',
          lineHeight: 1.7,
          margin:     '0 0 24px',
          maxWidth:   '380px',
        }}>
          {body}
        </p>

        {/* Button — shared Button component */}
        <Button
          variant="white"
          to={buttonHref ? undefined : buttonTo}
          href={buttonHref}
          size="md"
        >
          {buttonText}
        </Button>
      </div>

      {/* Spacer to maintain section height — hidden on mobile */}
      <div className="cta-spacer" style={{ minHeight: 'clamp(280px, 36vw, 440px)' }} />

      <style>{`
        @media (max-width: 767px) {
          .cta-section   { display: flex !important; flex-direction: column !important; min-height: unset !important; }
          .cta-photo     { position: relative !important; width: 100% !important; height: clamp(200px, 55vw, 280px) !important; bottom: auto !important; }
          .cta-green-box { position: relative !important; width: 100% !important; bottom: auto !important; padding: 28px 20px !important; }
          .cta-spacer    { display: none !important; }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .cta-green-box { width: 62% !important; }
          .cta-photo     { width: 62% !important; }
        }
      `}</style>
    </section>
  )
}